// In-module client picker for New Order (staff + reps). Ephemeral: the parent owns the selection
// as local state, so it starts empty each time you enter New Order — no global/persisted context.
import { useEffect, useRef, useState } from 'react';
import { fetchRepClients } from '../api/me.js';
import { storeShort } from '../lib/stores.js';
import { useStoreContext } from '../stores/storeContext.js';
import StoreSwitcher from './shell/StoreSwitcher.jsx';
import { ClientsIcon } from './icons.jsx';

const keyOf = (c) => c?.onBehalfOfClientUserId || c?.shopifyCompanyLocationId;

export default function ClientPicker({ value, onSelect }) {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const activeStore = useStoreContext((s) => s.activeStore); // '' = show every store

  useEffect(() => {
    fetchRepClients().then(setClients).catch(() => setClients([]));
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const needle = q.trim().toLowerCase();
  const shown = clients.filter(
    (c) =>
      (!activeStore || c.store === activeStore) &&
      (!needle ||
        `${c.companyName || ''} ${c.clientEmail || ''} ${c.locationName || ''}`.toLowerCase().includes(needle))
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm transition hover:border-line-strong"
      >
        <ClientsIcon size={15} />
        {value ? (
          <span className="font-medium text-text-primary">
            {value.companyName}
            {value.locationName && value.locationName !== value.companyName ? <span className="font-normal text-text-secondary"> · {value.locationName}</span> : null}
          </span>
        ) : (
          <span className="text-text-muted">Select a client…</span>
        )}
        <span className="text-text-muted">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 flex max-h-96 w-80 flex-col rounded-md border border-line bg-surface shadow-lg">
          <div className="space-y-1.5 border-b border-line p-1.5">
            <div className="flex justify-center">
              <StoreSwitcher />
            </div>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients…"
              className="w-full rounded border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </div>
          <div className="overflow-auto p-1">
            {shown.length === 0 ? (
              <div className="px-3 py-2 text-xs text-text-muted">{needle ? 'No matches.' : 'No clients available.'}</div>
            ) : (
              shown.map((c) => (
                <button
                  key={keyOf(c)}
                  type="button"
                  onClick={() => {
                    onSelect(c);
                    setOpen(false);
                    setQ('');
                  }}
                  className={`block w-full rounded px-3 py-2 text-left text-sm transition hover:bg-surface-raised ${keyOf(value) === keyOf(c) ? 'bg-accent-light' : ''}`}
                >
                  <div className="font-medium text-text-primary">{c.companyName}</div>
                  <div className="text-xs text-text-muted">
                    {c.locationName || 'Store'}{c.clientEmail ? ` · ${c.clientEmail}` : ''} · {storeShort(c.store)}
                    {c.currencyCode ? ` · ${c.currencyCode}` : ''}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
